import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, ArrowLeft, X, ShieldAlert, Download, BellRing, TriangleAlert, CircleCheck, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  date: "", days: 1, difficulty: "soft", location: "", coords: "",
  price: 0, spots_total: 10, spots_taken: 0, image: "",
  title: { it: "", en: "" }, description: { it: "", en: "" },
  itinerary: { it: "", en: "" },
  requirements: { min_tires: "street", winch: false, min_lift: "stock" },
};

const inputCls =
  "w-full bg-ink border border-line px-3.5 py-2.5 text-sm text-bone placeholder:text-faint focus:outline-none focus:border-blaze transition-colors duration-200";
const labelCls = "block font-telemetry text-[10px] uppercase tracking-[0.2em] text-sandmut mb-1.5";

const DIFF_STYLE = {
  soft: "text-[#7FB383] border-[#4E8752]/50 bg-[#4E8752]/10",
  trail: "text-ochre border-ochre/50 bg-ochre/10",
  extreme: "text-[#E86A50] border-[#D94235]/50 bg-[#D94235]/10",
};

export default function AdminPage() {
  const { t } = useLanguage();
  const { user, setAuthOpen } = useAuth();
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [joins, setJoins] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState(null);
  const [pins, setPins] = useState([]);
  const [pinUrl, setPinUrl] = useState("");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = !!(user && user.role === "admin");

  const load = () => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});
    axios.get(`${API}/admin/memberships`, { withCredentials: true }).then((r) => setJoins(r.data)).catch(() => {});
    axios.get(`${API}/admin/bookings`, { withCredentials: true }).then((r) => setBookings(r.data)).catch(() => {});
    axios.get(`${API}/admin/waitlist`, { withCredentials: true }).then((r) => setWaitlist(r.data)).catch(() => {});
    axios.get(`${API}/instagram/pinned`).then((r) => setPins(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (user === null) {
    return <div className="min-h-screen bg-ink" data-testid="admin-loading" />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="admin-denied">
        <div className="border border-line bg-surface p-10 max-w-md text-center">
          <ShieldAlert size={36} className="text-blaze mx-auto mb-5" />
          <h1 className="font-display text-3xl font-extrabold uppercase text-bone">{t.admin.denied}</h1>
          <p className="mt-3 text-sm text-ash">{t.admin.deniedSub}</p>
          <div className="mt-7 flex justify-center gap-3">
            <button
              data-testid="admin-login-btn"
              onClick={() => setAuthOpen(true)}
              className="font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-6 py-3 hover:bg-sand transition-colors"
            >
              {t.admin.loginAdmin}
            </button>
            <Link
              to="/"
              data-testid="admin-back-home-link"
              className="font-telemetry text-xs uppercase tracking-[0.2em] border border-line text-sand px-6 py-3 hover:border-blaze hover:text-blaze transition-colors"
            >
              {t.admin.back}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const openNew = () => {
    setForm(JSON.parse(JSON.stringify(EMPTY)));
    setEditId(null);
  };

  const openEdit = (ev) => {
    setForm({
      ...ev,
      itinerary: { it: ev.itinerary.it.join("\n"), en: ev.itinerary.en.join("\n") },
      requirements: { min_tires: "street", winch: false, min_lift: "stock", ...(ev.requirements || {}) },
    });
    setEditId(ev.id);
  };

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setN = (obj, k) => (e) => setForm((f) => ({ ...f, [obj]: { ...f[obj], [k]: e.target.value } }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      days: Number(form.days),
      price: Number(form.price),
      spots_total: Number(form.spots_total),
      spots_taken: Number(form.spots_taken),
      itinerary: {
        it: form.itinerary.it.split("\n").map((s) => s.trim()).filter(Boolean),
        en: form.itinerary.en.split("\n").map((s) => s.trim()).filter(Boolean),
      },
    };
    try {
      if (editId) {
        await axios.put(`${API}/admin/events/${editId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/events`, payload, { withCredentials: true });
      }
      toast.success(t.admin.saved);
      setForm(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.admin.error);
    } finally {
      setBusy(false);
    }
  };

  const addPin = async (e) => {
    e.preventDefault();
    if (!pinUrl.trim()) return;
    try {
      await axios.post(`${API}/admin/pinned`, { url: pinUrl.trim() }, { withCredentials: true });
      setPinUrl("");
      toast.success(t.admin.pinAdded);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.admin.invalidUrl);
    }
  };

  const delPin = async (p) => {
    try {
      await axios.delete(`${API}/admin/pinned/${p.id}`, { withCredentials: true });
      toast.success(t.admin.pinDeleted);
      load();
    } catch {
      toast.error(t.admin.error);
    }
  };

  const remind = async (ev) => {
    if (!window.confirm(t.admin.remindConfirm)) return;
    try {
      const { data } = await axios.post(`${API}/admin/events/${ev.id}/remind`, {}, { withCredentials: true });
      toast.success(`${t.admin.remindSent} (${data.sent})`);
    } catch {
      toast.error(t.admin.error);
    }
  };

  const del = async (ev) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    try {
      await axios.delete(`${API}/admin/events/${ev.id}`, { withCredentials: true });
      toast.success(t.admin.deleted);
      load();
    } catch {
      toast.error(t.admin.error);
    }
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-page">
      <header className="border-b border-line bg-surface/60">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Dust Republic" className="h-10 w-auto" />
            <span className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze border border-blaze/40 px-2.5 py-1">
              {t.admin.title}
            </span>
          </div>
          <Link
            to="/"
            data-testid="admin-back-home-link"
            className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-sandmut hover:text-blaze transition-colors"
          >
            <ArrowLeft size={14} />
            {t.admin.back}
          </Link>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex gap-2">
            <button
              data-testid="admin-tab-events"
              onClick={() => setTab("events")}
              className={`font-telemetry text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
                tab === "events" ? "bg-blaze text-ink border-blaze" : "border-line text-sandmut hover:text-blaze hover:border-blaze"
              }`}
            >
              {t.admin.tabEvents} ({events.length})
            </button>
            <button
              data-testid="admin-tab-joins"
              onClick={() => setTab("joins")}
              className={`font-telemetry text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
                tab === "joins" ? "bg-blaze text-ink border-blaze" : "border-line text-sandmut hover:text-blaze hover:border-blaze"
              }`}
            >
              {t.admin.tabJoins} ({joins.length})
            </button>
            <button
              data-testid="admin-tab-bookings"
              onClick={() => setTab("bookings")}
              className={`font-telemetry text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
                tab === "bookings" ? "bg-blaze text-ink border-blaze" : "border-line text-sandmut hover:text-blaze hover:border-blaze"
              }`}
            >
              {t.admin.tabBookings} ({bookings.length})
            </button>
            <button
              data-testid="admin-tab-waitlist"
              onClick={() => setTab("waitlist")}
              className={`font-telemetry text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
                tab === "waitlist" ? "bg-blaze text-ink border-blaze" : "border-line text-sandmut hover:text-blaze hover:border-blaze"
              }`}
            >
              {t.admin.tabWaitlist} ({waitlist.length})
            </button>
            <button
              data-testid="admin-tab-stats"
              onClick={() => {
                setTab("stats");
                axios.get(`${API}/admin/stats`, { withCredentials: true }).then((r) => setStats(r.data)).catch(() => {});
              }}
              className={`font-telemetry text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
                tab === "stats" ? "bg-blaze text-ink border-blaze" : "border-line text-sandmut hover:text-blaze hover:border-blaze"
              }`}
            >
              {t.admin.tabStats}
            </button>
            <button
              data-testid="admin-tab-media"
              onClick={() => setTab("media")}
              className={`font-telemetry text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
                tab === "media" ? "bg-blaze text-ink border-blaze" : "border-line text-sandmut hover:text-blaze hover:border-blaze"
              }`}
            >
              {t.admin.tabMedia} ({pins.length})
            </button>
          </div>
          {tab === "events" && !form && (
            <button
              data-testid="admin-new-event-btn"
              onClick={openNew}
              className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] bg-blaze text-ink px-5 py-2.5 hover:bg-sand transition-colors"
            >
              <Plus size={14} />
              {t.admin.newEvent}
            </button>
          )}
        </div>

        {tab === "events" && form && (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={save}
            data-testid="admin-event-form"
            className="border border-line bg-surface p-6 sm:p-8 mb-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between">
              <h2 className="font-display text-2xl font-extrabold uppercase text-bone">
                {editId ? `${t.admin.edit} — ${editId}` : t.admin.newEvent}
              </h2>
              <button type="button" data-testid="admin-event-cancel-btn" onClick={() => setForm(null)} className="text-sandmut hover:text-blaze transition-colors">
                <X size={18} />
              </button>
            </div>
            <div><label className={labelCls}>{t.admin.fields.titleIt}</label><input required data-testid="admin-field-title-it" value={form.title.it} onChange={setN("title", "it")} className={inputCls} /></div>
            <div><label className={labelCls}>{t.admin.fields.titleEn}</label><input required data-testid="admin-field-title-en" value={form.title.en} onChange={setN("title", "en")} className={inputCls} /></div>
            <div><label className={labelCls}>{t.admin.fields.location}</label><input required data-testid="admin-field-location" value={form.location} onChange={setF("location")} className={inputCls} /></div>
            <div><label className={labelCls}>{t.admin.fields.descIt}</label><textarea required rows={2} data-testid="admin-field-desc-it" value={form.description.it} onChange={setN("description", "it")} className={`${inputCls} resize-none`} /></div>
            <div><label className={labelCls}>{t.admin.fields.descEn}</label><textarea required rows={2} data-testid="admin-field-desc-en" value={form.description.en} onChange={setN("description", "en")} className={`${inputCls} resize-none`} /></div>
            <div><label className={labelCls}>{t.admin.fields.image}</label><input data-testid="admin-field-image" value={form.image} onChange={setF("image")} placeholder="https://…" className={inputCls} /></div>
            <div><label className={labelCls}>{t.admin.fields.itinIt}</label><textarea rows={3} data-testid="admin-field-itin-it" value={form.itinerary.it} onChange={setN("itinerary", "it")} className={`${inputCls} resize-none`} /></div>
            <div><label className={labelCls}>{t.admin.fields.itinEn}</label><textarea rows={3} data-testid="admin-field-itin-en" value={form.itinerary.en} onChange={setN("itinerary", "en")} className={`${inputCls} resize-none`} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>{t.admin.fields.date}</label><input required type="date" data-testid="admin-field-date" value={form.date} onChange={setF("date")} className={inputCls} /></div>
              <div><label className={labelCls}>{t.admin.fields.days}</label><input required type="number" min={1} data-testid="admin-field-days" value={form.days} onChange={setF("days")} className={inputCls} /></div>
              <div>
                <label className={labelCls}>{t.admin.fields.difficulty}</label>
                <select data-testid="admin-field-difficulty" value={form.difficulty} onChange={setF("difficulty")} className={`${inputCls} cursor-pointer`}>
                  <option value="soft" className="bg-ink">Soft Rover</option>
                  <option value="trail" className="bg-ink">Trail Raider</option>
                  <option value="extreme" className="bg-ink">Extreme Overland</option>
                </select>
              </div>
              <div><label className={labelCls}>{t.admin.fields.coords}</label><input data-testid="admin-field-coords" value={form.coords} onChange={setF("coords")} placeholder="43.06°N 11.61°E" className={inputCls} /></div>
              <div><label className={labelCls}>{t.admin.fields.price}</label><input required type="number" min={0} data-testid="admin-field-price" value={form.price} onChange={setF("price")} className={inputCls} /></div>
              <div><label className={labelCls}>{t.admin.fields.spots}</label><input required type="number" min={1} data-testid="admin-field-spots" value={form.spots_total} onChange={setF("spots_total")} className={inputCls} /></div>
            </div>
            <div className="sm:col-span-2 lg:col-span-3 border border-line p-5">
              <p className={`${labelCls} !text-blaze`}>{t.admin.reqTitle}</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>{t.admin.reqTires}</label>
                  <select
                    data-testid="admin-field-req-tires"
                    value={form.requirements.min_tires}
                    onChange={(e) => setForm((f) => ({ ...f, requirements: { ...f.requirements, min_tires: e.target.value } }))}
                    className={`${inputCls} cursor-pointer`}
                  >
                    {Object.entries(t.member.tiresOpts).map(([k, v]) => (
                      <option key={k} value={k} className="bg-ink">{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t.admin.reqLift}</label>
                  <select
                    data-testid="admin-field-req-lift"
                    value={form.requirements.min_lift}
                    onChange={(e) => setForm((f) => ({ ...f, requirements: { ...f.requirements, min_lift: e.target.value } }))}
                    className={`${inputCls} cursor-pointer`}
                  >
                    {Object.entries(t.member.liftOpts).map(([k, v]) => (
                      <option key={k} value={k} className="bg-ink">{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t.admin.reqWinch}</label>
                  <button
                    type="button"
                    data-testid="admin-field-req-winch"
                    onClick={() => setForm((f) => ({ ...f, requirements: { ...f.requirements, winch: !f.requirements.winch } }))}
                    className={`w-full font-telemetry text-[10px] uppercase tracking-[0.18em] border px-3 py-2.5 transition-colors ${
                      form.requirements.winch
                        ? "border-blaze/60 text-blaze bg-blaze/10"
                        : "border-line text-sandmut hover:text-blaze hover:border-blaze"
                    }`}
                  >
                    {form.requirements.winch ? "ON" : "OFF"}
                  </button>
                </div>
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-3 flex gap-3">
              <button
                type="submit"
                data-testid="admin-event-save-btn"
                disabled={busy}
                className="font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-8 py-3.5 hover:bg-sand transition-colors disabled:opacity-60"
              >
                {t.admin.save}
              </button>
              <button
                type="button"
                onClick={() => setForm(null)}
                className="font-telemetry text-xs uppercase tracking-[0.2em] border border-line text-sandmut px-8 py-3.5 hover:border-blaze hover:text-blaze transition-colors"
              >
                {t.admin.cancel}
              </button>
            </div>
          </motion.form>
        )}

        {tab === "events" && (
          <div className="flex flex-col gap-3" data-testid="admin-events-list">
            {events.map((ev) => (
              <div
                key={ev.id}
                data-testid={`admin-event-row-${ev.id}`}
                className="border border-line bg-surface/60 px-5 py-4 flex flex-wrap items-center gap-4 justify-between"
              >
                <div className="flex items-center gap-5 min-w-0">
                  <span className="font-telemetry text-[11px] text-ochre whitespace-nowrap">{ev.date}</span>
                  <div className="min-w-0">
                    <p className="font-display text-lg font-bold uppercase text-bone truncate">{ev.title.it}</p>
                    <p className="font-telemetry text-[10px] uppercase tracking-[0.15em] text-sandmut truncate">
                      {ev.location} — {ev.spots_taken}/{ev.spots_total} {t.admin.spotsLabel}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className={`font-telemetry text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${DIFF_STYLE[ev.difficulty]}`}>
                    {ev.difficulty}
                  </span>
                  <button
                    data-testid={`admin-event-edit-${ev.id}`}
                    onClick={() => openEdit(ev)}
                    className="border border-line p-2 text-sandmut hover:text-blaze hover:border-blaze transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={`admin-event-remind-${ev.id}`}
                    onClick={() => remind(ev)}
                    title={t.admin.remind}
                    className="border border-line p-2 text-sandmut hover:text-ochre hover:border-ochre transition-colors"
                  >
                    <BellRing size={14} />
                  </button>
                  <button
                    data-testid={`admin-event-delete-${ev.id}`}
                    onClick={() => del(ev)}
                    className="border border-line p-2 text-sandmut hover:text-[#E86A50] hover:border-[#D94235]/60 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "joins" && (
          <div className="flex flex-col gap-3" data-testid="admin-joins-list">
            {joins.length === 0 && (
              <p className="font-telemetry text-xs uppercase tracking-[0.2em] text-sandmut py-14 text-center">{t.admin.noJoins}</p>
            )}
            {joins.map((j) => (
              <div key={j.id} data-testid={`admin-join-row-${j.id}`} className="border border-line bg-surface/60 px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-bold uppercase text-bone">{j.name}</p>
                    <p className="font-telemetry text-[11px] text-ochre">{j.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-telemetry text-[11px] uppercase tracking-[0.15em] text-sand">{j.vehicle}</p>
                    <p className="font-telemetry text-[10px] uppercase tracking-[0.15em] text-sandmut">
                      {t.join.expOptions[j.experience] || j.experience} — {(j.created_at || "").slice(0, 10)}
                    </p>
                  </div>
                </div>
                {j.message && <p className="mt-2.5 text-xs text-ash leading-relaxed border-t border-line pt-2.5">{j.message}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div className="flex flex-col gap-6" data-testid="admin-bookings-list">
            {bookings.length > 0 && (
              <div className="flex justify-end">
                <button
                  data-testid="admin-export-csv-btn"
                  onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/admin/bookings.csv`, {
                        withCredentials: true,
                        responseType: "blob",
                      });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "dust-republic-presenze.csv";
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch {
                      toast.error(t.admin.error);
                    }
                  }}
                  className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] border border-blaze/50 text-blaze px-5 py-2.5 hover:bg-blaze hover:text-ink transition-colors"
                >
                  <Download size={14} />
                  {t.admin.exportCsv}
                </button>
              </div>
            )}
            {bookings.length === 0 && (
              <p className="font-telemetry text-xs uppercase tracking-[0.2em] text-sandmut py-14 text-center">
                {t.admin.noBookings}
              </p>
            )}
            {Object.entries(
              bookings.reduce((acc, b) => {
                (acc[b.event_id] = acc[b.event_id] || []).push(b);
                return acc;
              }, {})
            ).map(([evId, list]) => (
              <div key={evId} className="border border-line bg-surface/60">
                <div className="px-5 py-3.5 border-b border-line flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-lg font-bold uppercase text-bone">
                    {list[0].event_title?.it || evId}
                  </p>
                  <span className="font-telemetry text-[10px] uppercase tracking-[0.18em] text-ochre">
                    {list[0].event_date} — {list.length} {t.admin.tabBookings.toLowerCase()}
                    {list.some((b) => b.checkin_at) && (
                      <span className="text-[#7FB383]"> — {list.filter((b) => b.checkin_at).length} {t.admin.checkedIn}</span>
                    )}
                  </span>
                </div>
                {list.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`admin-booking-row-${b.id}`}
                    className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-3 border-b border-line/50 last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-semibold text-bone">{b.user_name || "—"}</p>
                      <p className="font-telemetry text-[11px] text-ochre">{b.user_email || "—"}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-telemetry text-[11px] uppercase tracking-[0.15em] text-sand">
                        {b.user_vehicle || "—"}
                      </p>
                      <p className="font-telemetry text-[10px] text-sandmut">
                        {t.admin.bookedOn} {(b.created_at || "").slice(0, 10)}
                      </p>
                      <span className="mt-1.5 flex items-center justify-end gap-3">
                        {b.rig_ok === false && (
                          <span className="flex items-center gap-1 font-telemetry text-[9px] uppercase tracking-[0.12em] text-[#E86A50]">
                            <TriangleAlert size={11} />
                            {t.admin.rigMismatch}
                          </span>
                        )}
                        {b.checkin_at && (
                          <span className="flex items-center gap-1 font-telemetry text-[9px] uppercase tracking-[0.12em] text-[#7FB383]">
                            <CircleCheck size={11} />
                            {t.admin.checkedIn}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {tab === "waitlist" && (
          <div className="flex flex-col gap-6" data-testid="admin-waitlist-list">
            {waitlist.length === 0 && (
              <p className="font-telemetry text-xs uppercase tracking-[0.2em] text-sandmut py-14 text-center">
                {t.admin.noWaitlist}
              </p>
            )}
            {Object.entries(
              waitlist.reduce((acc, w) => {
                (acc[w.event_id] = acc[w.event_id] || []).push(w);
                return acc;
              }, {})
            ).map(([evId, list]) => (
              <div key={evId} className="border border-line bg-surface/60">
                <div className="px-5 py-3.5 border-b border-line flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-lg font-bold uppercase text-bone">
                    {list[0].event_title?.it || evId}
                  </p>
                  <span className="font-telemetry text-[10px] uppercase tracking-[0.18em] text-ochre">
                    {list[0].event_date} — {list.length}
                  </span>
                </div>
                {list.map((w) => (
                  <div
                    key={w.id}
                    data-testid={`admin-waitlist-row-${w.id}`}
                    className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-3 border-b border-line/50 last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-semibold text-bone">{w.user_name || "—"}</p>
                      <p className="font-telemetry text-[11px] text-ochre">{w.user_email || "—"}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-telemetry text-[11px] uppercase tracking-[0.15em] text-sand">
                        {w.user_vehicle || "—"}
                        {w.user_rig && (
                          <span className="text-sandmut"> — {w.user_rig.tires?.toUpperCase()}{w.user_rig.winch ? " + WINCH" : ""}</span>
                        )}
                      </p>
                      <p className="font-telemetry text-[10px] text-sandmut">
                        {t.admin.waitingSince} {(w.created_at || "").slice(0, 10)}
                      </p>
                      {w.rig_ok === false && (
                        <span className="mt-1.5 flex items-center justify-end gap-1 font-telemetry text-[9px] uppercase tracking-[0.12em] text-[#E86A50]">
                          <TriangleAlert size={11} />
                          {t.admin.rigMismatch}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {tab === "stats" && (
          <div data-testid="admin-stats-panel">
            {!stats ? (
              <p className="font-telemetry text-xs uppercase tracking-[0.2em] text-sandmut py-14 text-center">…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-10">
                  {[
                    { label: t.admin.statMembers, value: stats.members },
                    { label: t.admin.statRequests, value: stats.requests },
                    { label: t.admin.statBookings, value: stats.bookings },
                    { label: t.admin.statCheckins, value: stats.checkins },
                    { label: t.admin.statWaiting, value: stats.waiting },
                    { label: t.admin.statSpots, value: `${stats.spots_taken}/${stats.spots_total}` },
                  ].map((s, i) => (
                    <div key={i} data-testid={`admin-stat-card-${i}`} className="border border-line bg-surface p-5">
                      <p className="font-display text-4xl font-black text-bone leading-none">{s.value}</p>
                      <p className="mt-2 font-telemetry text-[9px] uppercase tracking-[0.2em] text-sandmut">{s.label}</p>
                    </div>
                  ))}
                </div>
                <h3 className="flex items-center gap-2.5 font-display text-2xl font-extrabold uppercase tracking-tight text-bone mb-5">
                  <BarChart3 size={20} className="text-blaze" />
                  {t.admin.topEvents}
                </h3>
                <div className="flex flex-col gap-3">
                  {stats.top_events.map((ev) => (
                    <div key={ev.id} data-testid={`admin-top-event-${ev.id}`} className="border border-line bg-surface/60 px-5 py-4">
                      <div className="flex items-center justify-between mb-2.5">
                        <p className="font-display text-lg font-bold uppercase text-bone">{ev.title.it}</p>
                        <span className="font-telemetry text-[11px] text-ochre">{ev.spots_taken}/{ev.spots_total}</span>
                      </div>
                      <div className="h-[3px] bg-line w-full">
                        <div className="h-full bg-blaze" style={{ width: `${Math.min(100, (ev.spots_taken / Math.max(1, ev.spots_total)) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {tab === "media" && (
          <div data-testid="admin-media-panel" className="flex flex-col gap-6">
            <form onSubmit={addPin} className="border border-line bg-surface p-5 flex flex-col sm:flex-row gap-3">
              <input
                data-testid="admin-pin-url-input"
                value={pinUrl}
                onChange={(e) => setPinUrl(e.target.value)}
                placeholder={t.admin.pinLabel}
                className={`${inputCls} flex-1`}
              />
              <button
                type="submit"
                data-testid="admin-pin-add-btn"
                className="font-telemetry text-[11px] uppercase tracking-[0.2em] bg-blaze text-ink px-6 py-3 hover:bg-sand transition-colors whitespace-nowrap"
              >
                {t.admin.addPin}
              </button>
            </form>
            {pins.length === 0 && (
              <p className="font-telemetry text-xs uppercase tracking-[0.2em] text-sandmut py-14 text-center">
                {t.admin.noPins}
              </p>
            )}
            {pins.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-pin-row-${p.id}`}
                className="border border-line bg-surface/60 px-5 py-4 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="font-telemetry text-xs text-sand truncate">{p.url}</p>
                  <p className="font-telemetry text-[10px] text-sandmut mt-1">{(p.created_at || "").slice(0, 10)}</p>
                </div>
                <button
                  data-testid={`admin-pin-delete-${p.id}`}
                  onClick={() => delPin(p)}
                  className="border border-line p-2 text-sandmut hover:text-[#E86A50] hover:border-[#D94235]/60 transition-colors shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
